import { useEffect, useRef, useState, type CSSProperties } from "react";
import { Star } from "lucide-react";
import { useAchievements } from "@/os/state/achievements";
import { useToast } from "@/os/state/toast";

export function withBreaks(text: string): string {
    return text.replace(/([_.])/g, "$1\u200B");
}

export function SquiggleStrike({ className }: { className?: string }) {
    return (
        <svg className={className} viewBox="0 0 100 8" fill="none" preserveAspectRatio="none" aria-hidden>
            <path
                d="M2 5 C 20 1, 40 7, 58 4 S 90 2, 98 5"
                stroke="#FF6B6B"
                strokeWidth="3"
                strokeLinecap="round"
            />
        </svg>
    );
}

export function DoodleSquiggle({ className }: { className?: string }) {
    return (
        <svg className={className} width="90" height="26" viewBox="0 0 90 26" fill="none" aria-hidden>
            <path
                d="M3 18 C 12 4, 22 4, 30 14 S 48 24, 56 12 S 74 2, 87 12"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
            />
        </svg>
    );
}

export function DoodleArrow({ className }: { className?: string }) {
    return (
        <svg className={className} width="70" height="60" viewBox="0 0 70 60" fill="none" aria-hidden>
            <path
                d="M8 6 C 30 10, 48 22, 56 44"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeDasharray="1 7"
            />
            <path d="M48 38 L 57 46 L 60 34" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

export function DoodleStar() {
    const [pokes, setPokes] = useState(0);
    const [wig, setWig] = useState(false);
    const { trackEgg } = useAchievements();
    const { push } = useToast();
    const done = useRef(false);

    const poke = () => {
        setPokes((p) => p + 1);
        setWig(true);
        setTimeout(() => setWig(false), 420);
        const n = pokes + 1;
        if (n === 3 && !done.current) {
            done.current = true;
            trackEgg("egg_doodle_star");
            push("system", "you poked the star.", "three times. it found you back. that's an easter egg.");
        }
    };

    return (
        <button
            data-testid="doodle-star"
            aria-label="A hand-drawn star. Poke it."
            onClick={poke}
            className={`doodle !pointer-events-auto !opacity-60 hover:!opacity-100 ${wig ? "wiggle" : ""}`}
            style={{ right: 28, top: 24, color: "#EAB308" }}
        >
            <Star size={34} strokeWidth={2.2} fill={pokes > 0 ? "#FEF08A" : "none"} />
        </button>
    );
}

const TAB_COLORS = ["#EFF6FF", "#FFE8E8", "#F3E8FF", "#FEF08A", "#D1FAE5", "#FFEDD5"];

export function TabRain({ trigger }: { trigger: number }) {
    const [alive, setAlive] = useState(false);
    useEffect(() => {
        if (!trigger) return;
        setAlive(true);
        const t = setTimeout(() => setAlive(false), 3000);
        return () => clearTimeout(t);
    }, [trigger]);
    if (!alive) return null;
    const chips = Array.from({ length: 26 }, (_, i) => i);
    return (
        <div className="pointer-events-none absolute inset-0 z-[3500] overflow-hidden" data-testid="tab-rain" aria-hidden>
            {chips.map((i) => (
                <span
                    key={`${trigger}-${i}`}
                    className="tab-chip-fall"
                    style={
                        {
                            left: `${(i * 37) % 100}%`,
                            animationDelay: `${(i % 8) * 0.12}s`,
                            "--spin": `${(((i * 53) % 40) - 20)}deg`,
                            "--spin-end": `${(((i * 53) % 40) - 20 + ((i % 2) * 2 - 1) * 360)}deg`,
                        } as CSSProperties
                    }
                >
                    <span
                        className="flex items-center gap-1 rounded-t-md border-2 border-b-0 border-ink px-2 py-1 font-mono text-[9px] text-ink"
                        style={{ background: TAB_COLORS[i % TAB_COLORS.length] }}
                    >
                        <span className="h-1.5 w-1.5 rounded-full bg-coral" />
                        tab_{i + 1}.txt
                    </span>
                </span>
            ))}
        </div>
    );
}
